import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Zap, FileSpreadsheet, KeyRound, MoreHorizontal, Pencil, Compass, Boxes, Sigma, FunctionSquare, EyeOff, Eye, ChevronDown, ChevronRight, Plus } from "lucide-react";
import { typeIcon, effectiveType, displayName, isHiddenField, TYPE_OPTIONS } from "@/lib/fieldWells";
import { cn } from "@/lib/utils";

export const CARD_W = 240;
export const CARD_HEAD = 40;
export const FIELD_H = 22;
export const MAX_FIELDS = 10;

export function cardHeight(ds, expanded = false, measuresOpen = false, model = null) {
  const n = expanded ? (ds.schema || []).length : Math.min((ds.schema || []).length, MAX_FIELDS);
  const meas = measuresOpen ? ((model?.measures || []).length + (ds.calc_fields || []).length) * FIELD_H + 24 : 0;
  return CARD_HEAD + n * FIELD_H + 26 + 22 + meas;
}

function FieldPopover({ ds, field, model, onSaved, children }) {
  const fm = model?.field_meta || {};
  const meta = fm[field.name] || {};
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(meta.display_name || "");
  const save = async (patch) => {
    try { const { data } = await api.patch(`/datasets/${ds.id}/model/field-meta`, { field: field.name, patch }); onSaved(data.field_meta); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save field setting"); }
  };
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setName(meta.display_name || ""); }}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-64 p-3 space-y-2" align="start" data-testid="model-field-popover">
        <div className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] font-mono truncate">{field.name} · {field.type}</div>
        <div>
          <Label className="text-[11px]">Display name</Label>
          <Input data-testid="model-field-rename" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name !== (meta.display_name || "") && save({ display_name: name.trim() })}
            onKeyDown={(e) => e.key === "Enter" && save({ display_name: name.trim() })} placeholder={field.name} className="h-7 text-xs mt-1 bg-[color:var(--surface)]" />
        </div>
        <div>
          <Label className="text-[11px]">Type</Label>
          <Select value={meta.type_override || "__auto"} onValueChange={(v) => save({ type_override: v === "__auto" ? null : v })}>
            <SelectTrigger data-testid="model-field-type" className="h-7 text-xs mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="__auto">Auto ({field.type})</SelectItem>{TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-[11px]">Hidden from authors</Label>
          <Switch data-testid="model-field-hidden" checked={!!meta.hidden} onCheckedChange={(v) => save({ hidden: !!v })} />
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ModelCanvasCard({ ds, pos, keys, model, showHidden, onDragStart, onLoadModel, onFieldMeta, onRename, onAddMeasure, onAddCalc, onEditMeasure, onEditCalc, expanded, onToggleExpand, measuresOpen, onToggleMeasures }) {
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(ds.name);
  const fm = model?.field_meta || {};
  const schema = ds.schema || [];
  const visible = schema.filter((c) => showHidden || !isHiddenField(c.name, fm));
  const fields = expanded ? visible : visible.slice(0, MAX_FIELDS);
  const hiddenCount = schema.length - schema.filter((c) => !isHiddenField(c.name, fm)).length;
  const measures = (model?.measures || []).filter((m) => m.visible !== false);
  const calcs = ds.calc_fields || [];

  const rename = async () => {
    const v = newName.trim();
    if (!v || v === ds.name) { setRenaming(false); return; }
    try { await api.patch(`/datasets/${ds.id}`, { name: v }); toast.success("Dataset renamed"); onRename(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
    finally { setRenaming(false); }
  };

  return (
    <div data-testid="model-canvas-card" data-dataset-id={ds.id}
      className="absolute rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--glass-bg)] backdrop-blur-sm shadow-[var(--shadow-sm)] select-none"
      style={{ left: pos.x, top: pos.y, width: CARD_W, zIndex: expanded || measuresOpen ? 20 : 1 }}>
      <div onMouseDown={renaming ? undefined : onDragStart}
        className="flex items-center gap-1.5 px-3 h-10 rounded-t-xl cursor-move bg-[color:var(--surface-2)] border-b border-[color:var(--glass-border)]">
        {ds.mode === "live" ? <Zap className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-mint)]" /> : <FileSpreadsheet className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-ocean)]" />}
        {renaming ? (
          <Input data-testid="model-card-rename-input" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onBlur={rename}
            onKeyDown={(e) => { if (e.key === "Enter") rename(); if (e.key === "Escape") setRenaming(false); }} className="h-6 text-xs bg-[color:var(--surface)] flex-1" />
        ) : (
          <span data-testid="model-card-name" className="text-xs font-semibold text-[color:var(--ink)] truncate flex-1" onDoubleClick={() => { setNewName(ds.name); setRenaming(true); }}>{ds.name}</span>
        )}
        <span className="text-[9px] text-[color:var(--ink-3)]">{ds.row_count?.toLocaleString?.() || ""}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="model-card-menu" onMouseDown={(e) => e.stopPropagation()} className="p-0.5 rounded text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><MoreHorizontal className="w-3.5 h-3.5" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem data-testid="model-card-rename" onClick={() => { setNewName(ds.name); setRenaming(true); }}><Pencil className="w-3.5 h-3.5 mr-2" />Rename table</DropdownMenuItem>
            <DropdownMenuItem data-testid="model-card-add-measure" onClick={() => onAddMeasure(ds)}><Sigma className="w-3.5 h-3.5 mr-2" />New measure</DropdownMenuItem>
            <DropdownMenuItem data-testid="model-card-add-calc" onClick={() => onAddCalc(ds)}><FunctionSquare className="w-3.5 h-3.5 mr-2" />New calculated column</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild><a data-testid="model-card-explore" href={`/explore/${ds.id}`} target="_blank" rel="noreferrer"><Compass className="w-3.5 h-3.5 mr-2" />Explore data</a></DropdownMenuItem>
            <DropdownMenuItem asChild><a data-testid="model-card-studio" href={`/model/${ds.id}`} target="_blank" rel="noreferrer"><Boxes className="w-3.5 h-3.5 mr-2" />Full Model Studio</a></DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="py-1" onMouseEnter={() => !model && onLoadModel(ds.id)}>
        {fields.map((c) => {
          const eff = effectiveType(c.name, schema, fm);
          const Icon = typeIcon(eff);
          const isKey = keys.has(c.name);
          const hidden = isHiddenField(c.name, fm);
          return (
            <FieldPopover key={c.name} ds={ds} field={c} model={model} onSaved={(f) => onFieldMeta(ds.id, f)}>
              <button data-testid="model-card-field" data-field={c.name} onMouseDown={(e) => e.stopPropagation()}
                className={cn("w-full flex items-center gap-1.5 px-3 text-left hover:bg-[color:var(--surface-2)]", hidden && "opacity-50")} style={{ height: FIELD_H }}>
                {isKey ? <KeyRound className="w-3 h-3 shrink-0 text-amber-500" /> : <Icon className="w-3 h-3 shrink-0 text-[color:var(--ink-3)]" />}
                <span className={cn("text-[11px] truncate flex-1", isKey ? "text-[color:var(--ink)] font-medium" : "text-[color:var(--ink-2)]")}>{displayName(c.name, fm)}</span>
                {hidden && <EyeOff className="w-3 h-3 shrink-0 text-[color:var(--ink-3)]" />}
                {fm[c.name]?.type_override && <span className="text-[9px] text-[color:var(--brand-ocean)]">{fm[c.name].type_override}</span>}
              </button>
            </FieldPopover>
          );
        })}
        {visible.length > MAX_FIELDS && (
          <button data-testid="model-card-expand" onMouseDown={(e) => e.stopPropagation()} onClick={onToggleExpand} className="w-full px-3 text-left text-[10px] text-[color:var(--brand-ocean)] hover:underline" style={{ height: 20 }}>
            {expanded ? "Show fewer fields" : `+${visible.length - MAX_FIELDS} more fields`}
          </button>
        )}
        {hiddenCount > 0 && !showHidden && <div className="px-3 text-[10px] text-[color:var(--ink-3)] flex items-center gap-1" style={{ height: 20 }}><Eye className="w-3 h-3" />{hiddenCount} hidden</div>}
      </div>
      <div className="border-t border-[color:var(--glass-border)]">
        <button data-testid="model-card-measures-toggle" onMouseDown={(e) => e.stopPropagation()} onClick={() => { onToggleMeasures(); if (!model) onLoadModel(ds.id); }}
          className="w-full flex items-center gap-1 px-3 text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] hover:text-[color:var(--ink)]" style={{ height: 22 }}>
          {measuresOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}<Sigma className="w-3 h-3" />Measures {model ? `${measures.length}` : ""} · ƒx {calcs.length}
        </button>
        {measuresOpen && (
          <div className="pb-1" data-testid="model-card-measures">
            {measures.map((m) => (
              <button key={m.key} data-testid="model-card-measure" onMouseDown={(e) => e.stopPropagation()} onClick={() => onEditMeasure(ds, model, m)}
                className="w-full flex items-center gap-1.5 px-3 text-left hover:bg-[color:var(--surface-2)]" style={{ height: FIELD_H }}>
                <Sigma className="w-3 h-3 shrink-0 text-[color:var(--brand-ocean)]" />
                <span className="text-[11px] truncate flex-1 text-[color:var(--ink-2)]">{m.label || m.key}</span>
                <span className="text-[9px] text-[color:var(--ink-3)] font-mono truncate max-w-[70px]">{m.kind === "calculated" ? "calc" : `${m.agg}(${m.field})`}</span>
              </button>
            ))}
            {calcs.map((f) => (
              <button key={f.name} data-testid="model-card-calc" onMouseDown={(e) => e.stopPropagation()} onClick={() => onEditCalc(ds, f)}
                className="w-full flex items-center gap-1.5 px-3 text-left hover:bg-[color:var(--surface-2)]" style={{ height: FIELD_H }}>
                <FunctionSquare className="w-3 h-3 shrink-0 text-[color:var(--brand-mint)]" />
                <span className="text-[11px] truncate flex-1 text-[color:var(--ink-2)]">{f.name}</span>
                <span className="text-[9px] text-[color:var(--ink-3)]">column</span>
              </button>
            ))}
            <div className="flex gap-1 px-2 pt-1">
              <Button size="sm" variant="ghost" className="h-6 text-[10px] text-[color:var(--brand-ocean)] px-1.5" onMouseDown={(e) => e.stopPropagation()} onClick={() => onAddMeasure(ds)}><Plus className="w-3 h-3 mr-0.5" />Measure</Button>
              <Button size="sm" variant="ghost" className="h-6 text-[10px] text-[color:var(--brand-mint)] px-1.5" onMouseDown={(e) => e.stopPropagation()} onClick={() => onAddCalc(ds)}><Plus className="w-3 h-3 mr-0.5" />Calc column</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
